import React, { useMemo, useId } from 'react';
import PropTypes from 'prop-types';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { format, startOfHour, subHours } from 'date-fns';

/**
 * ActivityChart - Displays runs activity over time
 * Shows usage patterns by hour
 */
const ActivityChart = ({ runs, loading }) => {
  const gradientId = useId();

  // Group runs by hour
  const { chartData, peakHour, totalRuns } = useMemo(() => {
    if (!runs || runs.length === 0) {
      return { chartData: [], peakHour: null, totalRuns: 0 };
    }

    // Get the time range - last 24 hours
    const now = new Date();
    const hoursToShow = 24;
    const hourBuckets = {};

    // Initialize all hour buckets
    for (let i = hoursToShow - 1; i >= 0; i--) {
      const hourStart = startOfHour(subHours(now, i));
      const key = hourStart.getTime();
      hourBuckets[key] = {
        timestamp: key,
        count: 0,
        successCount: 0,
        errorCount: 0
      };
    }

    // Count runs per hour
    runs.forEach(run => {
      if (!run.created_at) return;
      const runTime = new Date(run.created_at);
      const hourStart = startOfHour(runTime);
      const key = hourStart.getTime();
      
      if (hourBuckets[key]) {
        hourBuckets[key].count++;
        if (run.status === 'success') {
          hourBuckets[key].successCount++;
        } else {
          hourBuckets[key].errorCount++;
        }
      }
    });

    // Convert to array and sort
    const data = Object.values(hourBuckets).sort((a, b) => a.timestamp - b.timestamp);
    
    // Find peak hour
    let peak = null;
    let maxCount = 0;
    data.forEach(d => {
      if (d.count > maxCount) {
        maxCount = d.count;
        peak = d;
      }
    });

    return {
      chartData: data,
      peakHour: peak,
      totalRuns: runs.length
    };
  }, [runs]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-lg p-3 shadow-lg bg-bg-secondary/95 border border-violet-500/30 backdrop-blur-sm">
          <p className="text-xs mb-2 text-text-tertiary">
            {format(new Date(data.timestamp), 'MMM d, h:00 a')}
          </p>
          <div className="space-y-1">
            <div className="flex justify-between gap-4">
              <span className="text-xs text-text-tertiary">Runs</span>
              <span className="text-sm font-mono font-bold text-violet-500">
                {data.count}
              </span>
            </div>
            {data.count > 0 && (
              <>
                <div className="flex justify-between gap-4">
                  <span className="text-xs text-text-tertiary">Success</span>
                  <span className="text-xs font-mono text-primary-500">
                    {data.successCount}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-xs text-text-tertiary">Errors</span>
                  <span className="text-xs font-mono text-red-500">
                    {data.errorCount}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }
    return null;
  };
  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array
  };

  if (loading && (!runs || runs.length === 0)) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary">
        <div className="h-4 w-32 rounded mb-4 animate-pulse bg-success-50"></div>
        <div className="h-[200px] rounded animate-pulse bg-primary-500/5"></div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-violet-500/25">
        <h3 className="text-sm font-semibold mb-4 text-text-primary">
          Activity Timeline
        </h3>
        <div className="h-[200px] flex flex-col items-center justify-center">
          <svg className="w-10 h-10 mb-2 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-text-tertiary">No activity data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-violet-500/25">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            Activity Timeline
          </h3>
          <p className="text-xs mt-0.5 text-text-tertiary">
            Last 24 hours
            {peakHour && peakHour.count > 0 && (
              <span> · Peak at {format(new Date(peakHour.timestamp), 'h a')}</span>
            )}
          </p>
        </div>
        <div className="text-right">
          <span className="text-lg font-bold font-mono text-violet-500">
            {totalRuns}
          </span>
          <p className="text-xs text-text-tertiary">
            total runs
          </p>
        </div>
      </div>
      
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id={`activityGradient-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="rgba(50, 205, 50, 0.1)" 
              vertical={false}
            />
            <XAxis 
              dataKey="timestamp"
              tickFormatter={(ts) => format(new Date(ts), 'ha')}
              stroke="#6B8E6B"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: 'rgba(50, 205, 50, 0.2)' }}
              tick={{ fill: '#6B8E6B' }}
              interval="preserveStartEnd"
            />
            <YAxis 
              stroke="#6B8E6B"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: 'rgba(50, 205, 50, 0.2)' }}
              tick={{ fill: '#6B8E6B' }}
              width={30}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="count"
              stroke="#8B5CF6"
              strokeWidth={2}
              fill={`url(#activityGradient-${gradientId})`}
              dot={false}
              activeDot={{ 
                r: 4, 
                fill: '#8B5CF6', 
                stroke: '#111', 
                strokeWidth: 2 
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

ActivityChart.propTypes = {
  runs: PropTypes.arrayOf(PropTypes.shape({
    created_at: PropTypes.string,
    status: PropTypes.string
  })),
  loading: PropTypes.bool
};

export default ActivityChart;
